import { TwoFactorService } from '../../services/two-factor.service';
import { TwoFactorBackupCodesDialogComponent } from '../two-factor-backup-codes-dialog/two-factor-backup-codes-dialog.component';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild
} from '@angular/core';
import { PplDialogService } from '@ppl/ui/dialog';
import { wrapAPICall } from '@ppl/utils/api.utils';
import { MemoizeLast } from '@ppl/utils/memoize.utils';
import { BehaviorSubject } from 'rxjs';
import { map } from 'rxjs/operators';
import * as srcDoc from 'srcdoc-polyfill';
import type { DialogResponse } from '@ppl/utils/dialog.utils';
import type { Observable } from 'rxjs';
import type { TwoFAMethod, TwoFAMethodValue } from '../../domain/two-factor';
import type { TwoFactorBackupCodesDialogData, TwoFactorBackupCodesDialogResponse } from '../two-factor-backup-codes-dialog/two-factor-backup-codes-dialog.component';

@Component({
    selector: 'ppl-two-factor-backup-codes',
    templateUrl: './two-factor-backup-codes.component.html',
    styleUrls: ['./two-factor-backup-codes.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class TwoFactorBackupCodesComponent {

  loadingCodes$ = new BehaviorSubject<boolean>(false);

  @Input() method: TwoFAMethod | null;
  @Input() securityToken: string;
  @Input() disabled = false;

  @Output() toggle = new EventEmitter<TwoFactorBackupCodesToggleEvent>();

  @ViewChild('printIFrame', { static: true }) printIFrame: ElementRef<HTMLIFrameElement>;

  @MemoizeLast<TwoFactorBackupCodesComponent>(['method'])
  get areBackupCodesEnabled() {
    return this.method
      ? this.method.enabled
      : false;
  }

  @MemoizeLast<TwoFactorBackupCodesComponent>(['method', 'securityToken'])
  get canAccessCodes() {
    return this.method && this.method.enabled && this.securityToken;
  }

  constructor(
    private dialogService: PplDialogService,
    private twoFactorService: TwoFactorService
  ) { }

  onShowCodes() {
    this.dialogService.open<
      TwoFactorBackupCodesDialogComponent,
      TwoFactorBackupCodesDialogData,
      DialogResponse<TwoFactorBackupCodesDialogResponse>
    >(TwoFactorBackupCodesDialogComponent, {
      data: {
        securityToken: this.securityToken,
        twoFactorMethodId: this.method.id
      }
    });
  }

  onToggle(enable: boolean) {
    this.toggle.emit({ method: this.method, enable });
  }

  onDownloadCodes() {
    this.loadBackupCodes().subscribe(codes => {
      this.twoFactorService.downloadBackupCodes(codes);
    });
  }

  onPrintCodes() {
    this.loadBackupCodes().subscribe(codes => {
      const content = this.twoFactorService.createBackupCodesFileContent(codes);
      const iframeLoadedCallback = () => {
        this.printIFrame.nativeElement.removeEventListener('load', iframeLoadedCallback);
        this.printIFrame.nativeElement.contentWindow.print();
      };
      this.printIFrame.nativeElement.addEventListener('load', iframeLoadedCallback);
      srcDoc.set(
        this.printIFrame.nativeElement,
        `<html><body><pre>${content}</pre></body></html>`,
        { force: false }
      );
    });
  }

  private loadBackupCodes(): Observable<TwoFAMethodValue[]> {
    return wrapAPICall(
      this.twoFactorService.getMethodValue({
        id: this.method.id,
        securityToken: this.securityToken
      }),
      this.loadingCodes$
    ).pipe(
      map(response => {
        return response.data;
      })
    );
  }
}

export interface TwoFactorBackupCodesToggleEvent {
  method: TwoFAMethod | null;
  enable: boolean;
}
